// constants.js — single source for BetMGM SlipSync boundary values
// Spec: docs/specs/slipsync-ui.md Section 6

export const VALID_STATES = [
  'nv', 'nj', 'pa', 'mi', 'co', 'tn', 'il', 'va',
  'wv', 'az', 'ia', 'wy', 'la', 'md', 'oh', 'ma', 'ky', 'nc',
];

export function getBetmgmWebViewUrl(state) {
  if (!VALID_STATES.includes(state)) throw new Error(`Invalid state: ${state}`);
  // The bet-history page, never the betting lobby (App Review 5.3): a 200 page under the same /en/sports scope as
  // the sync API (probed 2026-10-15); signed out, BetMGM's own sign-in takes over as before.
  return `https://www.${state}.betmgm.com/en/sports/my-bets`;
}

// #1409: the bridge's per-open nonce is a v4 UUID (platform.randomUUID). Anything else is refused before it can
// reach a script string.
const BRIDGE_NONCE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export function isBridgeNonce(nonce) {
  return typeof nonce === 'string' && BRIDGE_NONCE_RE.test(nonce);
}

// BetMGM blocks the combined typeFilter+pinnedBetslipIds query (302 -> /Account/AccessDenied), verified 2026-10-18
// (curl, x-bwin-sports-api: prod, NV, 3 of 3). Its own site now makes two calls, and these are their exact shapes
// and parameter order: OPEN has no typeFilter and keeps pinnedBetslipIds=; SETTLED has typeFilter=Settled and no
// pinnedBetslipIds. Never send both parameters together.
export const BETMGM_CALLS = Object.freeze(['open', 'settled']);
export function getBetmgmApiUrl(state, call = 'open', index = 1) {
  if (!VALID_STATES.includes(state)) throw new Error(`Invalid state: ${state}`);
  const base = `https://www.${state}.betmgm.com/en/sports/api/mybets/betslips?index=${index}&maxItems=6`;
  if (call === 'open') return `${base}&pinnedBetslipIds=&useGroupedView=true`;
  if (call === 'settled') return `${base}&typeFilter=Settled&useGroupedView=true`;
  throw new Error(`Invalid call: ${call}`);
}

// #1229: client-side cap on the /api/slipsync submit. The sheet is locked while
// SUBMITTING, so a hung request must not hold it forever. Aborting does not stop
// the server, which may still write bets.
export const SUBMIT_TIMEOUT_MS = 90000;
// #1712 PR A: the DK open-probe-only post (empty settled list) has its own short bound; it never shows a timeout.
export const OPEN_PROBE_POST_TIMEOUT_MS = 15000;

export const SYNC_STATUS = {
  IDLE: 'idle',
  OPENING_WEBVIEW: 'opening_webview',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const ERROR_MESSAGES = {
  AUTH_EXPIRED: 'BetMGM login required. Please try again.',
  BETMGM_API_ERROR: 'Unable to fetch BetMGM bets. Please try again.',
  NO_BETS_FOUND: 'No bets found on your BetMGM account.',
  SYNC_CANCELLED: 'Sync cancelled.',
  SYNC_TIMEOUT: 'Sync timed out. Please try again.',
  SUBMIT_TIMEOUT: 'SlipSync is taking longer than usual. Your bets may still show up; check again in a minute before syncing again.',
  OPEN_FAILED: 'Unable to open BetMGM. Please try again.',
  SESSION_EXPIRED: 'Session expired. Please log in again.',
  RATE_LIMITED: 'Already synced recently. Please wait before syncing again.',
  INVALID_DATA: 'Sync failed: invalid data. Please contact support.',
  NOT_AVAILABLE: 'Sync not available on your account.',
  UNAVAILABLE: 'Sync temporarily unavailable. Please try again shortly.',
  SERVER_ERROR: 'Sync failed: server error. Please try again.',
  NETWORK_ERROR: 'Sync failed: network error. Please check your connection and try again.',
  UNKNOWN_RESULT: 'Sync completed but result unknown.',
  // #1415: the SlipSync disclosure and the sportsbook session owner.
  CONSENT_REQUIRED: 'Agree to the SlipSync disclosure to sync your bets.',
  CONSENT_SAVE_FAILED: "We couldn't save your choice. Check your connection and try again.",
  CONSENT_CHECK_FAILED: "Couldn't check your SlipSync agreement. Try again.",
  CONSENT_REJECTED: "Couldn't save your choice. Try again later.",
  SESSION_RESET_FAILED: "Couldn't prepare the sportsbook page. Try again.",
  // Bridge hardening (spec §6): guard refusals, the kill switch, sign-in timeouts and the opening notice.
  SYNC_TOO_SOON: 'Try {book} again in {s} s.',
  SYNC_IN_FLIGHT: 'Another sync is running. Finish it first.',
  RECONNECT_REQUIRED: "Reconnect required. Make sure you can sign in on {book}'s own app or site, then tap Reconnect.",
  RECONNECT_COOLDOWN: 'Reconnect required. You can try again in {m} min.',
  TAKE_A_BREAK: "You've tried {book} a few times. Try again in {m} min.",
  BOOK_PAUSED: "{book} sync is paused. We're fixing it.",
  SIGNIN_TIMEOUT: "Sign-in didn't finish. Tap Sync to try again.",
  SYNC_NOTICE: "Opening {book}. Sign in if asked; we'll sync as soon as you're in.",
  // 2026-10-18: on My Bets, BetMGM refused the bets call (/Account/AccessDenied).
  BOOK_NO_BETS: "{book} didn't return your bets (code {code}). We've logged it.",
};
